// src/components/SelectModellingFeatures.js
import React, { useEffect, useMemo, useState, useCallback, memo } from 'react';
import { toast } from 'sonner';
import { cn } from 'lib/utils';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import { Checkbox } from 'components/ui/checkbox';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import LoadingState from 'components/shared/LoadingState';
import { getModellingFeatures, setPlayerStatsLabels } from '../../services/api';
import { POSITIONS } from '../../utils/constants';
import SaveBar, { useSaveShortcut, useUnsavedWarning } from './SaveBar';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const toTitleCase = (str = '') =>
  str.replace(/_/g, ' ').replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1));

// Shared cell sizing so the group-header checkboxes line up with row checkboxes.
const CELL_CLASSES = 'flex w-14 shrink-0 items-center gap-1.5';
// Faded tint for indeterminate (some-but-not-all) bulk checkboxes.
const TRI_STATE_CLASSES =
  'data-[state=indeterminate]:bg-primary/40 data-[state=indeterminate]:text-primary-foreground';

const triState = (rows, fields) => {
  let on = 0;
  let total = 0;
  rows.forEach((row) => {
    fields.forEach((field) => {
      total += 1;
      if (row[field]) on += 1;
    });
  });
  if (total === 0 || on === 0) return false;
  return on === total ? true : 'indeterminate';
};

// ─────────────────────────────────────────────────────────────────────────────
// Memoized Row Component - This prevents unnecessary re-renders
// ─────────────────────────────────────────────────────────────────────────────
const FeatureRow = memo(({ row, idx, onUpdate, onUpdateMany }) => {
  const handleCheckedChange = useCallback(
    (position) => (checked) => {
      onUpdate(idx, `modelling_${position}`, checked ? 1 : 0);
    },
    [idx, onUpdate]
  );

  const handleAllChange = useCallback(
    (checked) => {
      const updates = {};
      POSITIONS.forEach((position) => {
        updates[`modelling_${position}`] = checked ? 1 : 0;
      });
      onUpdateMany(idx, updates);
    },
    [idx, onUpdateMany]
  );

  const allState = triState([row], POSITIONS.map((p) => `modelling_${p}`));

  return (
    <div className="flex items-center gap-4 border-t border-borderGrey px-4 py-2 transition-colors hover:bg-neutral-50">
      {/* Field name and nicename stacked */}
      <div className="min-w-[200px] flex-1">
        <p className="text-token-base font-medium">{row.field}</p>
        <p
          className={cn(
            'text-token-sm text-muted-foreground',
            !row.stat_nicename && 'italic'
          )}
        >
          {row.stat_nicename || 'No nicename set'}
        </p>
      </div>

      {/* All + per-position checkboxes */}
      <div className="flex items-center gap-2">
        <label className={cn(CELL_CLASSES, 'cursor-pointer')}>
          <Checkbox
            className={TRI_STATE_CLASSES}
            checked={allState}
            onCheckedChange={handleAllChange}
          />
          <span className="text-token-sm text-muted-foreground">All</span>
        </label>
        {POSITIONS.map((position) => (
          <label key={position} className={cn(CELL_CLASSES, 'cursor-pointer')}>
            <Checkbox
              checked={Boolean(row[`modelling_${position}`])}
              onCheckedChange={handleCheckedChange(position)}
            />
            <span className="text-token-sm font-medium text-muted-foreground">{position}</span>
          </label>
        ))}
      </div>
    </div>
  );
});

FeatureRow.displayName = 'FeatureRow';

// ─────────────────────────────────────────────────────────────────────────────
// Group header — bulk select-all per position across the group's rows
// ─────────────────────────────────────────────────────────────────────────────
function GroupHeader({ items, onBulkSet }) {
  const rows = items.map(({ row }) => row);
  const indices = items.map(({ idx }) => idx);
  const allFields = POSITIONS.map((p) => `modelling_${p}`);

  return (
    <div className="flex items-center gap-4 border-t border-borderGrey bg-muted/50 px-4 py-1.5">
      <span className="min-w-[200px] flex-1 text-token-sm font-medium text-muted-foreground">
        Toggle all in group
      </span>
      <div className="flex items-center gap-2">
        <label className={cn(CELL_CLASSES, 'cursor-pointer')}>
          <Checkbox
            className={TRI_STATE_CLASSES}
            checked={triState(rows, allFields)}
            onCheckedChange={(checked) => onBulkSet(indices, allFields, checked ? 1 : 0)}
          />
          <span className="text-token-sm text-muted-foreground">All</span>
        </label>
        {POSITIONS.map((position) => (
          <label key={position} className={cn(CELL_CLASSES, 'cursor-pointer')}>
            <Checkbox
              className={TRI_STATE_CLASSES}
              checked={triState(rows, [`modelling_${position}`])}
              onCheckedChange={(checked) =>
                onBulkSet(indices, [`modelling_${position}`], checked ? 1 : 0)
              }
            />
            <span className="text-token-sm font-medium text-muted-foreground">{position}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
export default function SelectModellingFeatures({ onDirtyChange }) {
  const [allStats, setAllStats] = useState([]); // Store ALL stats
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // ─── Fetch ALL stats ───────────────────────────────────────────────────────
  const fetchFeatures = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { statsLabels: rows } = await getModellingFeatures();
      // Ensure the modelling position fields exist, default to 0 if not present
      const processedRows = (rows || []).map((row) => ({
        ...row,
        modelling_GKP: row.modelling_GKP ?? 0,
        modelling_DEF: row.modelling_DEF ?? 0,
        modelling_MID: row.modelling_MID ?? 0,
        modelling_FWD: row.modelling_FWD ?? 0,
      }));
      setAllStats(processedRows);
      setDirty(false);
    } catch (err) {
      console.error('Error fetching modelling features:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  // Report unsaved-changes state to the parent (tab-switch warning)
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useUnsavedWarning(dirty);

  // Memoized update functions to prevent recreating on each render
  const updateRow = useCallback((rowIdx, key, value) => {
    setDirty(true);
    setAllStats((prev) => {
      const newStats = [...prev];
      newStats[rowIdx] = { ...newStats[rowIdx], [key]: value };
      return newStats;
    });
  }, []);

  const updateRowMany = useCallback((rowIdx, updates) => {
    setDirty(true);
    setAllStats((prev) => {
      const newStats = [...prev];
      newStats[rowIdx] = { ...newStats[rowIdx], ...updates };
      return newStats;
    });
  }, []);

  const bulkSet = useCallback((indices, fields, value) => {
    setDirty(true);
    setAllStats((prev) => {
      const newStats = [...prev];
      indices.forEach((i) => {
        const updated = { ...newStats[i] };
        fields.forEach((field) => {
          updated[field] = value;
        });
        newStats[i] = updated;
      });
      return newStats;
    });
  }, []);

  // ─── Save features ─────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Submit ALL stats back to the database, not just the modelling ones
      await setPlayerStatsLabels({ statsLabels: allStats });
      setDirty(false);
      toast.success('Modelling features saved');
    } catch (err) {
      console.error('Error saving modelling features', err);
      toast.error('Failed to save modelling features');
    } finally {
      setSaving(false);
    }
  }, [allStats, saving]);

  // ─── Group rows for accordion display ─────────────────────────────────────
  // Filter to only show modelling features, but maintain indices from allStats
  const grouped = useMemo(() => {
    const groups = {};
    allStats.forEach((row, idx) => {
      // Only display rows where use_in_modelling is true/1
      if (!row.use_in_modelling) return;

      const groupName =
        row.type === 'fpl stat' ? 'FPL Stats' : toTitleCase(row.action_category1 || 'Other');
      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push({ row, idx }); // idx refers to position in allStats
    });
    return groups;
  }, [allStats]);

  // Count of modelling features for display
  const modellingCount = useMemo(
    () => allStats.filter((row) => row.use_in_modelling).length,
    [allStats]
  );

  const canSave = !loading && !loadError && modellingCount > 0;
  useSaveShortcut(handleSubmit, canSave);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="pb-24">
      {/* Header with Legend */}
      <div className="mb-4">
        <div className="mb-2 flex items-center gap-2">
          <h3 className="text-token-lg font-semibold">Modelling Features by Position</h3>
          {dirty && <Badge variant="warning">Unsaved changes</Badge>}
        </div>

        {/* Legend */}
        <div className="rounded-token-medium bg-muted px-4 py-3">
          <p className="text-token-base font-medium text-textSecondary">
            Select which positions each feature should be used for in modelling
          </p>
          <p className="text-token-sm text-muted-foreground">
            GKP = Goalkeeper, DEF = Defender, MID = Midfielder, FWD = Forward
          </p>
        </div>
      </div>

      {/* Accordions / Loader / Error / Empty */}
      {loading ? (
        <LoadingState message="Loading modelling features..." />
      ) : loadError ? (
        <Alert variant="destructive">
          <AlertTitle>Failed to load modelling features</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span>Something went wrong while fetching the data.</span>
            <Button variant="outline" size="sm" onClick={fetchFeatures}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      ) : modellingCount === 0 ? (
        <p className="p-6 text-center text-token-md text-muted-foreground">
          No modelling features found. Please ensure stats are marked for use in modelling.
        </p>
      ) : (
        <Accordion type="multiple" defaultValue={['FPL Stats']} className="space-y-2">
          {Object.entries(grouped).map(([groupName, items]) => (
            <AccordionItem
              key={groupName}
              value={groupName}
              className="overflow-hidden rounded-token-large border border-borderGrey bg-white"
            >
              <AccordionTrigger className="px-4 py-3 text-token-md font-semibold hover:no-underline">
                {groupName} ({items.length})
              </AccordionTrigger>
              <AccordionContent className="p-0">
                <GroupHeader items={items} onBulkSet={bulkSet} />
                {items.map(({ row, idx }) => (
                  <FeatureRow
                    key={`feature-${row.field}`}
                    row={row}
                    idx={idx}
                    onUpdate={updateRow}
                    onUpdateMany={updateRowMany}
                  />
                ))}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* Submit Button */}
      {canSave && (
        <SaveBar onSave={handleSubmit} saving={saving} label="Submit Features" />
      )}
    </div>
  );
}
